import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styles from './styles.css'
import VolumeSlider from 'components/VolumeSlider'
import VideoControls from 'components/VideoControls'
import cn from 'classnames'
import { throttle } from 'lodash'
import VideoStreamDisabledNotification from './VideoStreamDisabledNotification'
import LocalMediaOverlay from './LocalMediaOverlay'
import VisitEndingTimer from 'components/VisitEndingTimer'
import { SEC_IN_MIN, MS_IN_SEC } from 'constants/dateTime'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import {
  PROCESSING_END_VISIT,
  AWAITING_PARTICIPANT,
  AWAITING_PARTICIPANT_RECONNECT,
  VISIT_SUSPENDED_STATUS
} from 'constants/visitStatuses'
import { API_VERSION, SEND_STATS_INTERVAL_SECONDS } from 'constants/api'
import NetworkQuality from 'components/NetworkQuality'
import { isVisManager, notifyVisManager } from 'utils/visManager'
import {
  TOP_RIGHT,
  POSITION_STYLES,
  MUTE_TYPE_VIDEO,
  MUTE_TYPE_AUDIO,
  MUTE_TYPE_SPEAKER,
  WEBRTC_CALL_CONNECTED,
  WEBRTC_CALL_DISCONNECTED,
  PIP_POSITION_CHANGED,
  VIDEO_MUTED,
  VIDEO_UNMUTED,
  AUDIO_MUTED,
  AUDIO_UNMUTED,
  SPEAKER_MUTED,
  SPEAKER_UNMUTED
} from 'constants/visManager'
import platform from 'platform'
import { logError } from 'utils/datadog'
import { createVideoService } from 'services'
import { Events, Media, ConnectionType } from 'constants/videoService'
import { isIOS, isAndroid } from 'utils/user'

const messages = defineMessages({
  connectingStatus: 'Connecting...',
  awaitingParticipantStatus: 'Waiting for participant to join.',
  awaitingParticipantReconnectStatus: 'Waiting for participant to reconnect.',
  processingEndVisitStatus: 'Processing your visit. Please wait.',
  visitSuspendedStatus: 'Your visit is suspended.',
  confirmText: 'Are you sure?'
})

class Visit extends Component {
  constructor(props) {
    super(props)
    this.state = {
      micOn: true,
      camOn: true,
      phoneOn: true,
      remoteCamOn: true,
      isShowingControls: true,
      activeRoom: null,
      endingSoon: false,
      localNetworkQuality: -1,
      remoteNetworkQuality: -1,
      pipPosition: TOP_RIGHT,
      volumeValue: 100,
      endingVisitTimerId: null
    }
    this.localMedia = React.createRef()
    this.remoteMedia = React.createRef()
    this.onRoomConnectionSuccess = this.onRoomConnectionSuccess.bind(this)
    this.onRoomConnectionFailure = this.onRoomConnectionFailure.bind(this)
    this.onParticipantConnected = this.onParticipantConnected.bind(this)
    this.onParticipantDisconnected = this.onParticipantDisconnected.bind(this)
    this.toggleMic = this.toggleMic.bind(this)
    this.toggleCam = this.toggleCam.bind(this)
    this.hangUp = this.hangUp.bind(this)
    this.showVisitControls = throttle(this.showVisitControls.bind(this), 500)
    this.hideVisitControls = this.hideVisitControls.bind(this)
    this.toggleVisitControls = this.toggleVisitControls.bind(this)
    this.onTabClose = this.onTabClose.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    this.beforeUnload = this.beforeUnload.bind(this)
    this.sendToDataDogOnUnloadAndHide = this.sendToDataDogOnUnloadAndHide.bind(this)
    this.setNetworkQuality = this.setNetworkQuality.bind(this)
    this.changePipPosition = this.changePipPosition.bind(this)
    this.mute = this.mute.bind(this)
    this.onRemoteAudioTrackAttached = this.onRemoteAudioTrackAttached.bind(this)
    this.onRemoteVideoTrackAttached = this.onRemoteVideoTrackAttached.bind(this)
    this.onRoomDisconnected = this.onRoomDisconnected.bind(this)
  }

  async componentDidUpdate(prevProps, prevState) {
    if (!prevProps.webrtcToken && this.props.webrtcToken) {
      try {
        this.videoService = await this.setupVideoService()
        this.videoService.connect().then(this.onRoomConnectionSuccess, this.onRoomConnectionFailure)
      } catch(err) {
        this.props.showErrorModal()
      }
    }
    if (!prevProps.twilioRoomCreatedAt && this.props.twilioRoomCreatedAt) {
      this.setupVisitEndingTimer(this.props, false)
    }
    if (!!prevProps.scheduledEndTime && prevProps.scheduledEndTime !== this.props.scheduledEndTime) {
      this.setupVisitEndingTimer(this.props, true)
    }
    if (!prevProps.visitEnded && this.props.visitEnded) {
      window.removeEventListener('unload', this.onTabClose)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      window.removeEventListener('beforeunload', this.beforeUnload)
      if (isVisManager() && this.props.redirectURL) {
        window.location.replace(this.props.redirectURL)
      }
    }
    if (prevState.volumeValue !== this.state.volumeValue) {
      this.videoService.setVolume(this.state.volumeValue)
    }
  }

  componentDidMount() {
    window.addEventListener('unload', this.onTabClose)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('beforeunload', this.beforeUnload)
    window.pip = this.changePipPosition
    window.mute = this.mute
  }

  componentWillUnmount() {
    window.removeEventListener('unload', this.onTabClose)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('beforeunload', this.beforeUnload)
    window.pip = undefined
    window.mute = undefined
  }

  async setupVideoService() {
    // vendor name, twilio token and room name will come via WebSocket from GVS
    const videoService = await createVideoService(this.props.webrtcToken.provider, {
      webrtcToken: this.props.webrtcToken,
      roomName: this.props.roomName,
      connectionType: ConnectionType.VISIT,
      showVignette: this.props.useVignette && this.props.allowedToRecord,
      useBlurredVignette: this.props.useBlurredVignette,
      isFlex10Visit: this.props.isFlex10Visit,
      isSuspended: this.props.isSuspended,
      participantIds: this.props.participantIds
    })
    videoService.on(Events.ROOM_DISCONNECTED, this.onRoomDisconnected)
    videoService.on(Events.NETWORK_QUALITY_CHANGED, this.setNetworkQuality)
    videoService.on(Events.REMOTE_AUDIO_TRACK_ATTACHED, this.onRemoteAudioTrackAttached)
    videoService.on(Events.REMOTE_VIDEO_TRACK_ATTACHED, this.onRemoteVideoTrackAttached)
    videoService.on(Events.REMOTE_CAMERA_ENABLED, (enabled) => this.setState({ remoteCamOn: enabled }))
    videoService.on(Events.PARTICIPANT_CONNECTED, this.onParticipantConnected)
    videoService.on(Events.PARTICIPANT_DISCONNECTED, this.onParticipantDisconnected)
    // sending service reference to videoServiceMiddleware.js
    this.props.videoServiceInitialized(videoService)
    return videoService
  }

  setupVisitEndingTimer(props, isExtension) {
    if (isVisManager()) return
    const nowTSInSeconds = Math.trunc(Date.now() / MS_IN_SEC)
    let secondsTillTimer = props.scheduledEndTime - props.twilioRoomCreatedAt - SEC_IN_MIN
    if (isExtension) {
      clearTimeout(this.state.endingVisitTimerId)
      this.setState({ endingSoon: false })
      secondsTillTimer = props.scheduledEndTime - nowTSInSeconds - SEC_IN_MIN
    }
    const timerId = setTimeout(() => this.setState({ endingSoon: true }), secondsTillTimer * MS_IN_SEC)
    this.setState({ endingVisitTimerId: timerId })
  }

  changePipPosition(pos) {
    this.setState({ pipPosition: pos }, () => notifyVisManager(PIP_POSITION_CHANGED))
  }

  mute(media, bMute) {
    if (!this.state.activeRoom) return

    switch (media) {
      case MUTE_TYPE_VIDEO:
        this.videoService.mute(Media.VIDEO, bMute)
        this.setState({camOn: !bMute})
        this.props.toggleCamera(bMute ? 'off' : 'on')
        notifyVisManager(bMute ? VIDEO_MUTED : VIDEO_UNMUTED)
        break
      case MUTE_TYPE_AUDIO:
        this.videoService.mute(Media.AUDIO, bMute)
        this.setState({micOn: !bMute})
        this.props.toggleMicrophone(bMute ? 'off' : 'on')
        notifyVisManager(bMute ? AUDIO_MUTED : AUDIO_UNMUTED)
        break
      case MUTE_TYPE_SPEAKER:
        this.setState({volumeValue: bMute ? 0 : 100})
        notifyVisManager(bMute ? SPEAKER_MUTED : SPEAKER_UNMUTED)
        break
    }
  }

  sendToDataDogOnUnloadAndHide() {
    const {
      appointmentId
    } = this.props

    if (this.state.activeRoom) {
      logError(new Error('User closed tab, refreshed page or navigated away.'), {
        appointmentId: appointmentId,
        userId: this.state.activeRoom.localParticipant.identity,
        participantSid: this.state.activeRoom.localParticipant.sid,
        roomName: this.state.activeRoom.name,
        roomSID: this.state.activeRoom.sid
      })
    } else {
      logError(new Error('User closed tab, refreshed page or navigated away before joining the room.'), {
        appointmentId: appointmentId
      })
    }
  }

  async onVisibilityChange() {
    // send DataDog events when visibility changes to hidden and on page unload
    if (document.visibilityState === 'hidden') {
      this.sendToDataDogOnUnloadAndHide()
    }
    // on mobile restart local video when user returns to the page
    if (document.visibilityState === 'visible' && (isIOS() || isAndroid())) {
      await this.videoService.restartLocalVideo()
    }
  }

  beforeUnload() {
    this.sendToDataDogOnUnloadAndHide()
  }

  onTabClose() {
    const {
      appointmentId,
      visitStatus,
      userToken,
      jwt
    } = this.props
    const client = isVisManager() ? 'vis_manager' : 'visit_now'

    if (this.state.activeRoom) {
      this.state.activeRoom.disconnect()
    }

    if (!isVisManager()) {
      if  (this.state.activeRoom !== null || visitStatus === AWAITING_PARTICIPANT) {
        navigator.sendBeacon(`${process.env.GVS_URL}/api/client/${API_VERSION}/${client}/visits/end?token=${userToken}`)
      } else {
        navigator.sendBeacon(`${process.env.GVS_URL}/api/client/${API_VERSION}/${client}/visits/cancel?jwt=${jwt}&visit[appointment_id]=${appointmentId}`)
      }
    }
  }

  setNetworkQuality(type, value) {
    if (type === 'local') {
      this.setState({ localNetworkQuality: value })
    } else {
      this.setState({ remoteNetworkQuality: value })
    }
  }

  onRoomConnectionSuccess(room) {
    console.log(`Connected to Room "${room.name}"`)
    console.log(`LocalParticipantSID: "${room.localParticipant.sid}"`)

    this.storeStatsIntervalID = setInterval(() => {
      this.videoService.getStats().then(stats => {
        this.props.storeRoomStats(stats)
      })
    }, SEND_STATS_INTERVAL_SECONDS * MS_IN_SEC)

    if (this.props.isSuspended) {
      this.props.setVisitStatus(VISIT_SUSPENDED_STATUS)
    } else {
      this.props.setVisitStatus(null)
    }

    this.props.setUserTrackDetails({
      userId: +room.localParticipant.identity,
      audioDisabled: false,
      audioDisabledReason: null,
      videoDisabled: false,
      videoDisabledReason: null
    })
    this.props.storeClientInfo({
      participant_sid: room.localParticipant.sid,
      os_version: platform.os.version,
      os_family: platform.os.family,
      browser_version: platform.version,
      browser_family: platform.name,
      platform_client: 'web'
    })

    this.setState({ activeRoom: room })
    this.videoService.configureLocalParticipant(this.localMedia.current)
    this.videoService.configureRemoteParticipants([this.remoteMedia.current])

    notifyVisManager(WEBRTC_CALL_CONNECTED)
  }

  onRoomDisconnected(room, error) {
    clearInterval(this.storeStatsIntervalID)
    notifyVisManager(WEBRTC_CALL_DISCONNECTED)
    if (error) {
      error.message = `Room disconnected with error: ${error.code} - ${error.message}`
      logError(error, {
        appointmentId: this.props.appointmentId,
        userId: room.localParticipant.identity,
        participantSid: room.localParticipant.sid,
        roomName: room.name,
        roomSID: room.sid,
        errorCode: error.code,
        errorMessage: error.message
      })
      console.log(error.message)
    } else {
      logError(new Error('Room disconnected without error code.'), {
        appointmentId: this.props.appointmentId,
        userId: room.localParticipant.identity,
        participantSid: room.localParticipant.sid,
        roomName: room.name,
        roomSID: room.sid,
      })
    }
  }

  onParticipantConnected(participantId) {
    console.log(`Participant "${participantId}" connected`)
    if (isVisManager() && !this.props.isSuspended && this.props.participantIds.includes(participantId)) {
      this.props.setVisitStatus(null)
    }
  }

  onParticipantDisconnected(participantId) {
    console.log(`Participant "${participantId}" disconnected`)
    if (!this.props.visitEnded) {
      logError(new Error(`Participant "${participantId}" disconnected`), {
        appointmentId: this.props.appointmentId,
        userId: this.state.activeRoom.localParticipant.identity,
        participantSid: this.state.activeRoom.localParticipant.sid,
        roomName: this.state.activeRoom.name,
        roomSID: this.state.activeRoom.sid
      })
    }

    if (this.props.participantIds.includes(participantId)) {
      if (isVisManager()) {
        this.props.setVisitStatus(AWAITING_PARTICIPANT_RECONNECT)
      } else {
        this.props.setVisitStatus(PROCESSING_END_VISIT)
      }
    }
    this.resetRemoteTracksDetails(participantId)
  }

  onRemoteAudioTrackAttached() {
    this.videoService.setVolume(this.state.volumeValue)
  }

  onRemoteVideoTrackAttached(isEnabled) {
    const localParticipant = this.state.activeRoom.localParticipant
    const remoteParticipantId = this.props.participantIds.find(id => id !== +localParticipant.identity)

    this.setState({ remoteCamOn: isEnabled})
    this.props.setUserTrackDetails({
      userId: remoteParticipantId,
      videoDisabled: !isEnabled
    })
  }

  resetRemoteTracksDetails(remoteParticipantId) {
    this.setState({ remoteCamOn: true})
    this.props.setUserTrackDetails({
      userId: remoteParticipantId,
      audioDisabled: false,
      audioDisabledReason: null,
      videoDisabled: false,
      videoDisabledReason: null
    })
  }

  onRoomConnectionFailure(err) {
    console.warn('Failed to join room with error: ' + err)
    err.message = `Twilio ${err.code} - ${err.message}`
    logError(err, {
      appointmentId: this.props.appointmentId,
      roomName: this.props.roomName
    })
  }

  toggleMic() {
    this.mute(MUTE_TYPE_AUDIO, this.state.micOn)
  }

  toggleCam() {
    this.mute(MUTE_TYPE_VIDEO, this.state.camOn)
  }

  hangUp() {
    const {
      endVisit,
      redirectURL,
      userToken,
      confirmDialog
    } = this.props
    if (!userToken) return

    confirmDialog( () => {
      endVisit(() => {
        clearInterval(this.storeStatsIntervalID)
        this.setState({phoneOn: false })
        if (this.state.activeRoom === null && redirectURL) {
          window.location.replace(redirectURL)
        }
      })
    })
  }

  startHideControlsTimer() {
    this.setState({hideControlsTimeoutID: setTimeout(this.hideVisitControls, 5000)})
  }

  showVisitControls() {
    this.setState({isShowingControls: true})
    clearTimeout(this.state.hideControlsTimeoutID)
    this.startHideControlsTimer()
  }

  hideVisitControls() {
    this.setState({isShowingControls: false})
  }

  toggleVisitControls() {
    const visible = this.state.isShowingControls
    this.setState({isShowingControls: !visible}, () => {
      if (visible) {
        clearTimeout(this.state.hideControlsTimeoutID)
      } else {
        this.startHideControlsTimer()
      }
    })
  }

  render() {
    const {
      visitStatus,
      userTracks,
      hideControls
    } = this.props

    const localParticipant = this.state.activeRoom && this.state.activeRoom.localParticipant || null
    const remoteTracks = localParticipant && userTracks ? userTracks.find(t => t.userId !== +localParticipant.identity) : {}

    return (
      <div styleName='videoPage' onClick={this.toggleVisitControls} onMouseMove={this.showVisitControls}>

        { visitStatus && (
          <h2 styleName='videoTitle'>
            <FormattedMessage {...messages[visitStatus]} />
          </h2>
        )}

        <div styleName='visitContent'>
          <div styleName='videoInputContainer' id='localMedia' ref={this.localMedia} style={POSITION_STYLES[this.state.pipPosition]} />
          { this.state.localNetworkQuality >= 0 && (
            <div styleName='localNetworkQualityContainer'>
              <NetworkQuality qualityLevel={this.state.localNetworkQuality} className={styles.localNetworkQuality} />
            </div>
          )}

          <div styleName='videoOutputContainer' id='remoteMedia' ref={this.remoteMedia} />
          { this.state.remoteNetworkQuality >= 0 && (
            <div styleName='remoteNetworkQualityContainer'>
              <NetworkQuality qualityLevel={this.state.remoteNetworkQuality} className={styles.remoteNetworkQuality} />
            </div>
          )}
        </div>

        <LocalMediaOverlay
          camOff={!this.state.camOn}
          micOff={!this.state.micOn}
          position={this.state.pipPosition}
        />

        <div styleName='countdownTimer'>
          { this.state.activeRoom && this.state.endingSoon && (
            <VisitEndingTimer />
          )}
        </div>

        { !hideControls && (
          <div styleName={cn('visitControls', {showControls: this.state.isShowingControls, hideControls: !this.state.isShowingControls})}
               onClick={(e) => e.stopPropagation()}
          >
            <div styleName='volumeSlider'>
              <VolumeSlider
                value={this.state.volumeValue}
                setValue={(value) => this.setState({volumeValue: value})}
              />
            </div>
            <div styleName='visitMenu'>
              <VideoControls
                micOn={this.state.micOn}
                camOn={this.state.camOn}
                phoneOn={this.state.phoneOn}
                toggleMic={this.toggleMic}
                toggleCam={this.toggleCam}
                hangUp={this.hangUp}
                hideHangUpButton={isVisManager()}
              />
            </div>
          </div>
        )}


        { !this.state.remoteCamOn && (
          <VideoStreamDisabledNotification
            remoteTracks={remoteTracks}
            remoteVideoDisabled={!this.state.remoteCamOn}
          />
        )}
      </div>
    )
  }
}

Visit.propTypes = {
  webrtcToken: PropTypes.object,
  roomName: PropTypes.string,
  toggleMicrophone: PropTypes.func,
  toggleCamera: PropTypes.func
}

export default injectIntl(Visit)
